import type React from "react";
import {useState} from "react";
import {IconButton, Skeleton, Stack, Tooltip} from "@mui/material";
import {OpenInNew} from "@mui/icons-material";
import {ClickToEdit} from "~/components/ClickToEdit/ClickToEdit";
import {notificationStore} from "~/stores/init";
import {useUpdateSupplierEnrichmentMutation} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useUpdateSupplierEnrichmentMutation";

const PLACEHOLDER = 'Add a domain';

/** The stored value may or may not carry a scheme; the link needs one either way. */
function domainHref(domain: string): string {
    return domain.startsWith('http') ? domain : `https://${domain}`;
}

type DomainFieldProps = {
    entityId: number;
    /** `EnrichmentProfile` id — undefined while loading, or when the entity has no profile at all. */
    profileId: number | undefined;
    domain: string;
    isLoading: boolean;
};

/**
 * Click-to-edit Domain — the second user-editable enrichment field, added 2026-09-14 (§2.5).
 * Lives on `contact_info.domain`, but the edit actions take it as a top-level `domain` key.
 *
 * Editing takes the text itself, so the "open in a new tab" affordance moved to its own icon:
 * a click on the value opens the editor, which would otherwise swallow the link.
 */
export const DomainField: React.FC<DomainFieldProps> = ({entityId, profileId, domain, isLoading}) => {
    const {mutate} = useUpdateSupplierEnrichmentMutation();

    const [displayDomain, setDisplayDomain] = useState(domain);
    const [prevDomain, setPrevDomain] = useState(domain);
    if (prevDomain !== domain) {
        setPrevDomain(domain);
        setDisplayDomain(domain);
    }

    if (isLoading) return <Skeleton variant="text" width={160}/>;

    const onSave = (value: string) => {
        const trimmed = value.trim();
        if (trimmed === displayDomain.trim()) return;

        const previous = displayDomain;
        setDisplayDomain(trimmed);
        mutate(
            {entityId, profileId, domain: trimmed},
            {
                onError: () => {
                    setDisplayDomain(previous);
                    notificationStore.show('Could not save the domain. Please try again.', {
                        severity: 'error',
                        id: `supplier-domain-error-${entityId}`,
                        autoHideDuration: 3000,
                    });
                },
            },
        );
    };

    return (
        <Stack direction="row" alignItems="center" gap={0.5} sx={{minWidth: 0}}>
            <ClickToEdit
                text={displayDomain}
                onSave={onSave}
                placeholder={PLACEHOLDER}
                typographyProps={{variant: 'body2'}}
                inputSx={{height: '24px !important'}}
            />
            {displayDomain && (
                <Tooltip title="Open in a new tab">
                    <IconButton
                        size="small"
                        component="a"
                        href={domainHref(displayDomain)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open ${displayDomain} in a new tab`}
                        sx={{flexShrink: 0}}
                    >
                        <OpenInNew sx={{fontSize: 14}}/>
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );
};
